import {
  BookOpen,
  Bot,
  Code,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  Send,
  Radio,
  Settings,
  Users,
} from 'lucide-react';
import type { SidebarNavGroup } from '@zafroi/ui-kit';

/**
 * Sidebar nav config for ZAFROI Engage.
 *
 * Five operational areas, each with a distinct job, no overlap:
 *   Dashboard → overview / what to act on
 *   Customers → identity + history
 *   Support   → the one action queue
 *   Channels / Chatbot / Settings → setup
 *
 * Removals (preserved as page code for revival):
 *
 *   - "Leads" (2026-05-24) — CRM territory; Engage is not a CRM. Code in
 *     `src/modules/leads/`.
 *   - "Tickets" (2026-05-24) — helpdesk territory (SLA, assignee, grouping);
 *     Engage is not a ticketing tool. Every inbound flow already creates a
 *     support request. Code in `src/modules/tickets/`; `EngageTicket`
 *     Prisma model retained for compliance-driven revival.
 *   - "Conversations" (2026-05-24) — read-only audit view with no actions.
 *     Action surface is Support (Phase 4 Conversation panel shows the
 *     thread inside each support request). Bot-only chats (portal
 *     conversations that never escalated) now live on the Chatbot page's
 *     Insights tab as "Bot activity" — the bot's audit surface alongside
 *     its setup, gaps, and drafts. Customer-level conversation history
 *     belongs on the Customer profile. Page code preserved at
 *     `src/modules/conversations/` and routes still resolve, so the
 *     `/conversations/:id` detail view is reachable via deep-link from
 *     those surfaces.
 */
export const sidebarNav: SidebarNavGroup[] = [
  {
    id: 'overview',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        href: '/dashboard',
      },
    ],
  },
  {
    id: 'engagement',
    label: 'Engagement',
    items: [
      { id: 'customers', label: 'Customers', icon: Users, href: '/customers' },
    ],
  },
  {
    id: 'support',
    items: [
      {
        id: 'support',
        label: 'Support',
        icon: LifeBuoy,
        href: '/support',
      },
    ],
  },
  {
    id: 'setup',
    label: 'Setup',
    items: [
      { id: 'channels', label: 'Channels', icon: Radio, href: '/channels' },
      { id: 'chatbot', label: 'Chatbot', icon: Bot, href: '/chatbot' },
      { id: 'email-templates', label: 'Email templates', icon: Mail, href: '/email-templates' },
      { id: 'sent-emails', label: 'Sent emails', icon: Send, href: '/sent-emails' },
      { id: 'settings', label: 'Settings', icon: Settings, href: '/settings' },
    ],
  },
  // Developer surfaces — how tenants extend / integrate Engage with their
  // own apps + websites. Mirrors the Operations app's developer console
  // pattern (Reference / Keys / Webhooks / Logs) but scoped to Engage's
  // public surface. The chatbot is the first integration that lives here;
  // future entries (webhooks, API reference, audit logs) slot in alongside.
  {
    id: 'developer',
    label: 'Developer',
    items: [
      {
        id: 'chatbot-embed',
        label: 'Embed widget',
        icon: Code,
        href: '/chatbot/embed',
      },
      {
        id: 'chatbot-reference',
        label: 'API reference',
        icon: BookOpen,
        href: '/chatbot/reference',
      },
      {
        id: 'api-keys',
        label: 'API keys',
        icon: KeyRound,
        href: '/developer/api-keys',
      },
    ],
  },
];

export const navIcons = {
  Inbox,
};
